import _ from 'lodash';
import * as coreBi from '#packages/coreBi';
import * as stateManagement from '#packages/stateManagement';
import constants from '#packages/constants';
import { api as menuApi, utils } from '#packages/menu';
import { isOneOfSelectedIsDropDown } from '#packages/util/menu';
import type {
  IPageMenuItem,
  IMenuConfig,
  GetActionsOptions,
} from '#packages/menu';
import {
  isCustomMenusEnabled,
  isMainMenuFlowEnabled,
  editorWixRecorder,
  link,
} from '#packages/util';
import { DesignerPanelsApiKey } from '#packages/apis';

import pagesAPI from '../../../api/api';
import {
  PAGE_CATEGORIES as pageCategories,
  sendNavigateToPageBiEvent,
} from '../../../utils';
import * as menuDataGetterUtil from '../utils/menuDataGetterUtil';
import * as menuViewUtils from './menuViewUtils';
import type { EditorAPI } from '#packages/editorAPI';
import type { BiEventDefinition, BiEventFields } from 'types/bi';
import { TriggerSources } from '#packages/pages';
import { DefaultActionTypes } from '../../types';
import { getIsReadonlyModePage } from '@/sectionsMigration';
import { generateBIForAppID } from './menuViewUtils';
import {
  getAppPagesSections,
  getMainPagesIds,
  pageIsInLinkPanelBlackList,
} from '@/panels';

const { LinkTypes } = link;

const { createMenuApi } = menuApi;

const {
  getLinkPanelPages,
  getAddMenuItemsActions,
  getAddAppsMenuItemsActions,
} = utils;

const origin = 'pagesPanel';

interface ItemClickHandler {
  (
    editorAPI: EditorAPI,
    biCategory: string,
    menuId: string,
    selectedMenuItemId: string,
    onItemAdded: () => void,
  ): () => void;
}

interface AppItemClickHandler {
  (
    appID: string,
  ): (
    editorAPI: EditorAPI,
    biCategory: string,
    menuId: string,
    selectedMenuItemId: string,
    onItemAdded: () => void,
  ) => () => void;
}

const sendBIEvent = (
  editorAPI: EditorAPI,
  biCategory: string,
  category: string,
  event: BiEventDefinition,
  biParams?: BiEventFields,
) => {
  editorAPI.bi.event(event, {
    ...biParams,
    origin,
    category,
    source: biCategory,
  });
};

const sendBIEventOnClick = (
  editorAPI: EditorAPI,
  biCategory: string,
  category: string,
  biParams?: BiEventFields,
) => {
  const event = coreBi.events.pages.pagesPanel.add_page_selection_click;

  sendBIEvent(editorAPI, biCategory, category, event, biParams);
};

const sendBIEventOnHover = (
  editorAPI: EditorAPI,
  biCategory: string,
  category: string,
  biParams?: BiEventFields,
) => {
  const event = coreBi.events.pages.pagesPanel.hover_on_item_in_context_menu;

  sendBIEvent(editorAPI, biCategory, category, event, biParams);
};

const getPagesWithVerticals = (editorAPI: EditorAPI) => {
  const pages = utils.getPagesWithVerticalsDynamic(editorAPI);
  const mainPagesIds = getMainPagesIds(pages);
  const finalPages = pages.filter((page) => !mainPagesIds.includes(page.id));
  return getAppPagesSections(editorAPI, finalPages);
};

const getOnClickAddAnchor: ItemClickHandler =
  (editorAPI, biCategory, menuId, selectedMenuItemId, onItemAdded) => () => {
    const options = { defaultLinkType: LinkTypes.ANCHOR_LINK };
    editorAPI.menus.addLink(menuId, selectedMenuItemId, onItemAdded, options);
    sendBIEventOnClick(editorAPI, biCategory, 'anchor');
  };

const getOnClickAddSection: ItemClickHandler =
  (editorAPI, biCategory, menuId, selectedMenuItemId, onItemAdded) => () => {
    const options = { defaultLinkType: LinkTypes.ANCHOR_LINK };
    editorAPI.menus.addLink(menuId, selectedMenuItemId, onItemAdded, options);
    sendBIEventOnClick(editorAPI, biCategory, 'section');
  };

const getOnClickAddDynamic: ItemClickHandler =
  (editorAPI, biCategory, menuId, selectedMenuItemId, onItemAdded) => () => {
    const options = { defaultLinkType: LinkTypes.PAGE_LINK };
    editorAPI.menus.addLink(menuId, selectedMenuItemId, onItemAdded, options);
    sendBIEventOnClick(editorAPI, biCategory, 'dynamic');
  };

const getOnClickAddLink: ItemClickHandler =
  (editorAPI, biCategory, menuId, selectedMenuItemId, onItemAdded) => () => {
    const options = { defaultLinkType: LinkTypes.EXTERNAL_LINK };
    editorAPI.menus.addLink(menuId, selectedMenuItemId, onItemAdded, options);
    sendBIEventOnClick(editorAPI, biCategory, 'link');
  };

const getSubPages = (editorAPI: EditorAPI, pageId: string) => {
  const appData = editorAPI.pages.data.get(pageId);
  const appDefinitionId = appData?.appDefinitionId ?? appData?.managingAppDefId;
  const subPages = stateManagement.tpaDynamicPages.selectors.getFocusedSubPages(
    editorAPI.store.getState(),
    appDefinitionId,
    pageId,
    'linkPanel',
  );

  return subPages.map((key) => ({
    key,
  }));
};

const getTpaInnerRoutes = (
  editorAPI: EditorAPI,
  appDefinitionId: string,
  pageId: string,
  subPage: string,
) => {
  const innerRoutes =
    stateManagement.tpaDynamicPages.selectors.getSubPageInnerRoute(
      editorAPI.store.getState(),
      appDefinitionId,
      pageId,
      subPage,
    );
  return innerRoutes ?? [];
};

const getOnClickAddAppLink: AppItemClickHandler =
  (appID: string) =>
  (editorAPI, biCategory, menuId, selectedMenuItemId, onItemAdded) =>
  () => {
    const verticalsPages = getPagesWithVerticals(editorAPI);
    const installedAppsIDs = verticalsPages.map((vertical) => vertical.id);
    const appIndex = installedAppsIDs.indexOf(appID);

    const page = verticalsPages[appIndex].pages.find(
      (page) => !pageIsInLinkPanelBlackList(appID, page),
    );
    const routerId = editorAPI.dsRead.routers.getRouterDataForPageIfExist(
      page.id,
    )?.routerId;
    const innerRouteState = {
      routerId: routerId ?? null,
      tpaSubPages: [] as { key: string }[],
      selectedTpaSubPage: '',
    };
    if (!routerId) {
      innerRouteState.tpaSubPages =
        // if no onTpaPageSelect passes - handle it as regular link
        page?.managingAppDefId ? getSubPages(editorAPI, page.id) : [];
      innerRouteState.selectedTpaSubPage = innerRouteState.tpaSubPages[0]?.key;
    }

    let link;
    if (routerId) {
      link = {
        routerId,
        type: 'DynamicPageLink',
        pageId: `#${page.id}`,
        innerRoute: '/',
      };
    } else if (innerRouteState.tpaSubPages.length > 0) {
      const tpaInnerRoutes = getTpaInnerRoutes(
        editorAPI,
        page.appDefinitionId ?? page.managingAppDefId,
        page.id,
        innerRouteState.selectedTpaSubPage,
      );
      const firstRoute = tpaInnerRoutes?.[0];

      link = {
        type: 'TpaPageLink',
        pageId: `#${page.id}`,
        appDefinitionId: page.managingAppDefId,
        itemTypeIdentifier: firstRoute?.itemTypeIdentifier,
        itemId: firstRoute?.itemId,
        path: firstRoute?.path,
      };
    } else {
      link = {
        type: 'PageLink',
        pageId: `#${page.id}`,
      };
    }

    const options = { defaultLinkType: LinkTypes.DYNAMIC_PAGE_LINK, link };
    editorAPI.menus.addLink(menuId, selectedMenuItemId, onItemAdded, options);

    const appIDForBI = generateBIForAppID(appID);
    sendBIEventOnClick(editorAPI, biCategory, `app_page_${appIDForBI}`);
  };

const getOnClickAddFolder: ItemClickHandler =
  (editorAPI, biCategory, menuId, selectedMenuItemId, onItemAdded) => () => {
    editorWixRecorder.addLabel('pages_panel_dnd_add_folder');
    editorAPI.menus.addFolder(menuId, selectedMenuItemId, onItemAdded);
    sendBIEventOnClick(editorAPI, biCategory, 'dropdown menu');
  };

const getOnClickAddPage =
  (editorAPI: AnyFixMe, origin: string, biCategory: string) =>
  (clickOrigin: AnyFixMe) => {
    pagesAPI.openAddPagePanel(editorAPI, {
      origin,
      biCategory,
      clickOrigin,
    });
    sendBIEventOnClick(editorAPI, biCategory, 'page', {
      page_id: null,
      button: clickOrigin,
    });
  };

const getPageId = (editorAPI: EditorAPI) => {
  return editorAPI.dsRead.pages.getFocusedPageId();
};

const navigateToPage = (
  editorAPI: EditorAPI,
  pageId: AnyFixMe,
  biCategory: string,
  panelName: string,
) => {
  const newCurPageId = getPageId(editorAPI);

  if (newCurPageId !== pageId) {
    editorAPI.pages.navigateTo(pageId);
    sendNavigateToPageBiEvent(editorAPI, {
      pageId,
      biCategory,
      panelName,
    });
  }
};

/** Same logic written here
 * pages/panel/pagesView/editorAPIConnectors/popupView.ts#L129
 * plase take it in to account when updating
 */
const navigateToPopUp = (
  editorAPI: EditorAPI,
  pageId: string,
  biCategory: string,
  panelName: string,
) => {
  const popupId = pageId.replace('#', '');
  const newCurPageId = getPageId(editorAPI);

  if (newCurPageId !== popupId) {
    editorAPI.pages.navigateTo(popupId, () => {
      editorAPI.selection.selectComponentByCompRef(
        editorAPI.pages.popupPages.getPopupContainer(popupId),
      );
    });
    sendNavigateToPageBiEvent(editorAPI, {
      pageId: popupId,
      biCategory,
      panelName,
    });
  }
};

const navigateToAnchor = (
  editorAPI: EditorAPI,
  pageId: string,
  biCategory: string,
  panelName: string,
  anchorDataId: string,
) => {
  const newCurPageId = getPageId(editorAPI);

  if (newCurPageId !== pageId || anchorDataId) {
    editorAPI.pages.navigateToAndScroll(
      pageId,
      anchorDataId,
      editorAPI.scroll.applyPreviewScrollToEditorScroll,
    );
    sendNavigateToPageBiEvent(editorAPI, {
      pageId,
      biCategory,
      panelName,
    });
  }
};

const navigateToDynamicPage = (
  editorAPI: EditorAPI,
  routerId: AnyFixMe,
  innerRoute: AnyFixMe,
  biCategory: AnyFixMe,
  panelName: AnyFixMe,
) => {
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line you-dont-need-lodash-underscore/assign
  const dynamicPageByRouter = _.assign(
    editorAPI.dsRead.data.createItem('DynamicPageLink'),
    {
      innerRoute: innerRoute || '',
      routerId,
    },
  );
  editorAPI.pages.navigateTo(dynamicPageByRouter);
  sendNavigateToPageBiEvent(editorAPI, {
    biCategory,
    panelName,
  });
};

const getShouldShowAddFolderButton = (isInMultiLingualFlow: boolean) => {
  if (isInMultiLingualFlow) {
    return false;
  }

  if (isCustomMenusEnabled()) {
    return isMainMenuFlowEnabled();
  }

  return true;
};

const mapStateToProps = (
  { editorAPI }: AnyFixMe,
  { isDesktop, menu, collapsionMap }: AnyFixMe,
) => {
  const isOldMenu = isOneOfSelectedIsDropDown({ editorAPI });
  const metaSiteId = editorAPI.site.getMetaSiteId();
  const loggedUserId = editorAPI.dsRead.generalInfo.getUserInfo()?.userId;
  const membersAppDefId =
    editorAPI.dsRead.platform.editorApps.SANTA_MEMBERS.appDefId;
  const renderedMenuItems = menuViewUtils.getMenuItemsWithInitialCollaptionData(
    menu.menuItems,
    collapsionMap,
  );
  const renderedMenuItemsWithSymbol =
    menuViewUtils.getMenuItemsWithsymbolIfApplicable(
      renderedMenuItems,
      menu.appDefId,
      membersAppDefId,
    );
  const biCategory = pageCategories.MENUS.getBiCategory(menu.id);
  const isInMultiLingualFlow =
    editorAPI.language.multilingual.isEnabled() &&
    !stateManagement.multilingual.services.utils.currentIsOriginal(editorAPI);

  const shouldShowAddFolderButton =
    getShouldShowAddFolderButton(isInMultiLingualFlow);

  const shouldShowAddLinkButton =
    !isCustomMenusEnabled() && !isInMultiLingualFlow;

  const showDynamicPages = getLinkPanelPages(editorAPI, false).length > 0;
  const loadingPageId = editorAPI.pages.loading.getPageId();
  const loadingTrigger = editorAPI.pages.loading.getTrigger();
  const setLoadingTrigger = (nonePageId?: string) => {
    editorAPI.pages.loading.setTrigger({
      source: TriggerSources.MENU_ITEM,
      nonePageId,
    });
  };

  return {
    staticPagesCount: editorAPI.pages.getStaticPagesCount(),
    title: menu.title,
    menuItems: renderedMenuItemsWithSymbol,
    isDraggable: isDesktop,
    renameEnabled: isDesktop,
    isMobile: !isDesktop,
    initialActions: !isDesktop && menu.appDefId ? null : ([] as AnyFixMe[]),
    showAddOptions:
      isDesktop &&
      menu.appDefId !==
        editorAPI.dsRead.platform.editorApps.SANTA_MEMBERS.appDefId,
    biCategory,
    titleAdd: pageCategories.MENUS.titleAdd,
    getTooManyPagesFrequencyCount:
      stateManagement.userPreferences.selectors.getSessionUserPreferences(
        constants.USER_PREFS.TOO_MANY_PAGES.FREQUENCY_COUNT,
      )(editorAPI.store.getState()) || 0,
    shouldShowAddFolderButton,
    shouldShowAddLinkButton,
    showDynamicPages,
    loadingPageId,
    setLoadingTrigger,
    loadingTrigger,
    isOldMenu,
    metaSiteId,
    loggedUserId,
  };
};

const getEditorAPI = (
  dispatch: AnyFixMe,
  getState: AnyFixMe,
  { editorAPI }: AnyFixMe,
): EditorAPI => editorAPI;

const mapDispatchToProps = (
  dispatch: AnyFixMe,
  {
    onShowSettings,
    onItemAdded,
    onRename,
    isDesktop,
    onHelp,
    menu,
    selectedMenuItemId,
    biPanelName,
    setInPrefIscollapsedMenuItems,
  }: AnyFixMe,
) => {
  const editorAPI: EditorAPI = dispatch(getEditorAPI);
  const state = editorAPI.store.getState();
  const useOriginalLanguage = editorAPI.menus.shouldUseOriginalLanguage();
  const rawMenuData = editorAPI.dsRead.menu.getById(
    menu.id,
    useOriginalLanguage,
  );
  const membersAppDefId =
    editorAPI.dsRead.platform.editorApps.SANTA_MEMBERS.appDefId;
  const biCategory = pageCategories.MENUS.getBiCategory(menu.id);
  const contextMenuActions = stateManagement.contextMenu.actions;
  const isEditorLoadedWithMoreThanMaxStaticPages =
    !!stateManagement.pages.selectors.getIsEditorLoadedWithMoreThanMaxStaticPages(
      state,
    );
  const menuAPI = createMenuApi(editorAPI);
  const isTranslating =
    stateManagement.multilingual.selectors.isTranslating(state);

  function createOnClickHandler(callback: ItemClickHandler) {
    return callback(
      editorAPI,
      biCategory,
      menu.id,
      selectedMenuItemId,
      onItemAdded,
    );
  }

  function createAddAppOnClickHandler(callback: AppItemClickHandler) {
    return (appID: string) =>
      callback(appID)(
        editorAPI,
        biCategory,
        menu.id,
        selectedMenuItemId,
        onItemAdded,
      );
  }

  const onAddLinkClick = createOnClickHandler(getOnClickAddLink);
  const onAddFolderClick = createOnClickHandler(getOnClickAddFolder);
  const onAddAnchorClick = createOnClickHandler(getOnClickAddAnchor);
  const onAddDynamicClick = createOnClickHandler(getOnClickAddDynamic);
  const onAddSectionClick = createOnClickHandler(getOnClickAddSection);
  const onAddAppPageClick = createAddAppOnClickHandler(getOnClickAddAppLink);

  const getAddItemsActions = (
    option: GetActionsOptions,
    config?: IMenuConfig,
  ) => {
    return getAddMenuItemsActions(
      {
        ...option,
        onAddLinkClick,
        onAddAnchorClick,
        onAddDynamicClick,
        onAddSectionClick,
        onAddAppPageClick,
        onAddDropdownClick: onAddFolderClick,
      },
      config,
    );
  };

  const getAddAppsItemsActions = (option: GetActionsOptions) => {
    return getAddAppsMenuItemsActions(
      {
        ...option,
        onAddLinkClick,
        onAddAnchorClick,
        onAddDynamicClick,
        onAddSectionClick,
        onAddAppPageClick,
        onAddDropdownClick: onAddFolderClick,
      },
      getPagesWithVerticals(editorAPI),
    );
  };

  const onAddPagesSelectionClick = (category: string) => {
    sendBIEventOnClick(editorAPI, biCategory, category);
  };

  const onContextMenuItemsHover = (category: string, target: string) => {
    const pageId = getPageId(editorAPI);

    sendBIEventOnHover(editorAPI, biCategory, category, {
      target,
      page_id: pageId,
    });
  };

  return {
    onContextMenuItemsHover,
    onAddPagesSelectionClick,
    onComponentMount: () => {
      if (isCustomMenusEnabled()) {
        editorWixRecorder.addLabel('custom_menus');
        editorWixRecorder.addLabel('pages_panel_open');
        editorWixRecorder.addCustomAttribute(
          'custom_menus_number',
          String(menuAPI.getCustomMenus().length),
        );
      }
    },
    isEditorLoadedWithMoreThanMaxStaticPages,
    biEvent: editorAPI.bi.event,
    openHelpCenter: editorAPI.panelManager.openHelpCenter,
    openPanel: editorAPI.panelManager.openPanel,
    onMoveMenuItem: (
      fromId: AnyFixMe,
      toId: AnyFixMe,
      position: AnyFixMe,
      callback: AnyFixMe,
    ) =>
      editorAPI.menus.moveMenuItem(
        rawMenuData,
        fromId,
        toId,
        position,
        true,
        callback,
      ),
    navigateCallback: (menuItem: AnyFixMe) => {
      if (menuItem.type.isPage) {
        navigateToPage(
          editorAPI,
          menuItem.pageData.id,
          biCategory,
          biPanelName,
        );
      } else if (menuItem.type.isPopupLink) {
        navigateToPopUp(
          editorAPI,
          menuItem.link.pageId,
          biCategory,
          biPanelName,
        );
      } else if (menuItem.type.isLink && menuItem.type.raw === 'AnchorLink') {
        const anchorLink = menuItem.link;
        const pageId = anchorLink.pageId && anchorLink.pageId.replace('#', '');
        navigateToAnchor(
          editorAPI,
          pageId,
          biCategory,
          biPanelName,
          anchorLink.anchorDataId,
        );
      } else if (
        menuItem.type.isLink &&
        menuItem.type.raw === 'DynamicPageLink'
      ) {
        navigateToDynamicPage(
          editorAPI,
          menuItem.link.routerId,
          menuItem.link.innerRoute,
          biCategory,
          biPanelName,
        );
      }
    },
    findMenuItemById: editorAPI.menus.findMenuItemById,
    mainMenuPanelContext: editorAPI.mainMenu.panelContext,
    titleAdd: pageCategories.MENUS.titleAdd,
    titleAddMenuItem: pageCategories.MENUS.titleAddMenuItem,
    titleNewPage: pageCategories.MENUS.titleNewPage,
    titleDropdownPlaceholder: pageCategories.MENUS.titleDropdownPlaceholder,
    onHelpHeader: _.partial(
      onHelp,
      menuDataGetterUtil.getHelpIdForMenu(
        menu.id,
        menu.appDefId,
        membersAppDefId,
        isDesktop,
      ),
    ),
    onHelp,
    onClickAddPage: getOnClickAddPage(editorAPI, origin, biCategory),
    onAddLinkClick,
    getAddItemsActions,
    getAddAppsItemsActions,
    openContextMenu: (config: AnyFixMe) =>
      editorAPI.store.dispatch(contextMenuActions.openContextMenu(config)),
    setInPrefIscollapsedMenuItems,
    notifyDataBindingAppTooManyPages:
      editorAPI.pages.notifyDataBindingAppTooManyPages,
    getMenuItemActions: (menuItem: IPageMenuItem) => {
      const menuItemActions = pagesAPI.menus.getActions(
        editorAPI,
        isDesktop,
        isTranslating,
        onShowSettings,
        onItemAdded,
        onRename,
        menuItem,
        menu,
      );

      const isReadonlyModePage = getIsReadonlyModePage(menuItem.pageData?.id);

      if (isReadonlyModePage) {
        return menuItemActions.filter(
          (action) =>
            !('isSettings' in action && action.isSettings) &&
            !('isTools' in action && action.isTools) &&
            !('isCmsAction' in action && action.isCmsAction) &&
            action.defaultActionType !==
              DefaultActionTypes.SET_AS_HOMEPAGE_TYPE,
        );
      }

      return menuItemActions;
    },
    setTooManyPagesFrequencyCount: (frequencyCount: AnyFixMe) =>
      editorAPI.store.dispatch(
        stateManagement.userPreferences.actions.setSessionUserPreferences(
          constants.USER_PREFS.TOO_MANY_PAGES.FREQUENCY_COUNT,
          frequencyCount,
        ),
      ),
    showUserActionNotification: (...args: AnyFixMe[]) =>
      dispatch(
        stateManagement.notifications.actions.showUserActionNotification(
          //@ts-expect-error
          ...args,
        ),
      ),
    openSiteGeneratorPlayground: () =>
      editorAPI.host.getAPI(DesignerPanelsApiKey).openSiteGeneratorPlayground(),
    openUpdateSectionsPanel: () =>
      editorAPI.host.getAPI(DesignerPanelsApiKey).openUpdateSectionsPanel(),
  };
};

export { mapStateToProps, mapDispatchToProps };
